import { NJIconButton } from '@engie-group/fluid-design-system-react';
import type { PlanningConstraint } from '@shared/size2mix-shell';
import type { GanttRow, PlanningView } from './types';

/* Constraint colours (data-viz, hex allowed) — one per Nemo legend entry. */
export const CONSTRAINT_COLORS: Record<string, string> = {
    Indisponible: '#9e9e9e',
    Minimum: '#6dcff6',
    Maximum: '#f29e2e',
    'Forcé à': '#9d7bd8',
    'Forcé au minimum à': '#00a6ca',
    'Forcé au maximum à': '#d7191c',
    'Démarrage interdit': '#1a1a1a',
};

function range(view: PlanningView) {
    const start = Date.UTC(2026, 9, 1);
    if (view === 'day') return { start, end: start + 86400000, ticks: ['00:00', '06:00', '12:00', '18:00'] };
    if (view === 'week') return { start, end: start + 7 * 86400000, ticks: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] };
    if (view === 'month') return { start, end: Date.UTC(2026, 10, 1), ticks: ['1 oct.', '8 oct.', '15 oct.', '22 oct.', '29 oct.'] };
    return { start, end: Date.UTC(2027, 9, 1), ticks: ['oct.', 'janv.', 'avr.', 'juil.', 'oct.'] };
}

export function Gantt({
    rows,
    constraints,
    view,
    onDelete,
}: {
    rows: GanttRow[];
    constraints: PlanningConstraint[];
    view: PlanningView;
    onDelete: (id: string) => void;
}) {
    const { start, end, ticks } = range(view);
    const pct = (date: string) => Math.min(100, Math.max(0, ((Date.parse(date) - start) / (end - start)) * 100));
    const border = '1px solid var(--nj-semantic-color-border-neutral-minimal-default)';

    return (
        <div style={{ overflowX: 'auto', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '219px 1fr', minWidth: 740 }}>
                <div style={{ borderBottom: border, borderRight: border }} />
                <div style={{ borderBottom: border, fontSize: 12 }}>
                    <div style={{ height: 26, display: 'flex', gap: 44, paddingLeft: 24 }}><span>2026</span><span>2027</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', height: 32 }}>{ticks.map((tick, index) => <span key={`${tick}-${index}`}>{tick}</span>)}</div>
                </div>
                {rows.map((row) => {
                    const rowConstraints = constraints.filter((c) => c.target === row.label);
                    return (
                        <div key={row.id} style={{ display: 'contents' }}>
                            <div
                                style={{
                                    height: 34,
                                    boxSizing: 'border-box',
                                    padding: '7px 0',
                                    paddingLeft: 31 + row.level * 15,
                                    fontWeight: 400,
                                    fontSize: 16,
                                    lineHeight: '19px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    borderBottom: border,
                                    borderRight: border,
                                }}
                            >
                                {row.label}
                            </div>
                            <div
                                style={{
                                    position: 'relative',
                                    height: 34,
                                    borderBottom: border,
                                    backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent calc(100% / 12 - 1px), var(--nj-semantic-color-border-neutral-minimal-default) calc(100% / 12 - 1px), var(--nj-semantic-color-border-neutral-minimal-default) calc(100% / 12))`,
                                }}
                            >
                                {rowConstraints.map((c) => {
                                    const knownDates = !!c.start && !!c.end;
                                    const left = knownDates ? pct(c.start) : 0;
                                    const width = knownDates ? Math.max(0.5, pct(c.end) - left) : 0;
                                    if (knownDates && width <= 0.5 && (Date.parse(c.end) < start || Date.parse(c.start) > end)) return null;
                                    return (
                                        <div
                                            key={c.id}
                                            title={knownDates ? `${c.kind}${c.value != null ? ` ${c.value} MW` : ''} · ${c.start} → ${c.end}` : `${c.kind} — dates non relevées, position illustrative`}
                                            style={{
                                                position: 'absolute',
                                                top: 6,
                                                bottom: 6,
                                                left: knownDates ? `${left}%` : 27,
                                                width: knownDates ? `${width}%` : 150,
                                                background: CONSTRAINT_COLORS[c.kind],
                                                color: 'var(--nj-core-color-white)',
                                                fontSize: 12,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '0 2px 0 8px',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {c.kind}
                                                {c.value != null ? ` ${c.value} MW` : ''}
                                            </span>
                                            <NJIconButton icon="close" label={`Supprimer la contrainte ${c.kind}`} variant="inverse" scale="2xs" onClick={() => onDelete(c.id)} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function PlanningLegend({ items }: { items: readonly string[] }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13 }}>
            {items.map((k) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 14, height: 14, background: CONSTRAINT_COLORS[k], display: 'inline-block' }} />
                    {k}
                </span>
            ))}
        </div>
    );
}
